import { Suspense, lazy, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import NavBadge from '@/layouts/partials/NavBadge'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import {
  useCanCreateAnotherCompany,
  useCompanySelection,
} from '@/lib/hooks/useCompanySelection'
import { useMyPendingJoinRequests } from '@/lib/hooks/useJoinRequestStatus'
import { useAuth } from '@/lib/provider/AuthProvider'
import {
  ADD_COMPANY_STATE,
  CREATE_COMPANY_PATH,
} from '@/lib/hooks/useRequireCompany'
import { useTenant } from '@/lib/provider/TenantProvider'
import { cn } from '@/lib/misc/Utils'
import ChevronUpDownIcon from '@icons/chevron-up-down'
import ClockIcon from '@icons/clock'

const CompanySwitcher = lazy(() => import('./CompanySwitcher'))
const JoinCompanyDialog = lazy(() => import('./JoinCompanyDialog'))

export const COMPANY_PROFILE_PATH = '/settings/company-profile'

/**
 * The organization the navigation should name. While a customer's portal is
 * being viewed, that is the customer — not the signed-in user's own company,
 * which stays put for the whole impersonation.
 */
export const SET_UP_COMPANY_LABEL = 'Set up company'

export const useOrganizationName = (): string => {
  const [auth] = useAuth()
  const { customerId, tenantName } = useTenant()
  const pendingRequests = useMyPendingJoinRequests()

  // The tenant's profile is fetched, so its name is briefly unknown. Falling
  // back to the user's own company there would name the wrong organization.
  if (customerId) return tenantName ?? '…'

  if (auth.user.company?.name) return auth.user.company.name

  const pendingRequest = pendingRequests[0]
  if (pendingRequest) return `Waiting on ${pendingRequest.customer_name}`

  return SET_UP_COMPANY_LABEL
}

interface OrganizationButtonState {
  isAwaitingApproval: boolean
}

interface OrganizationButtonProps {
  className?: string
  wrapperClassName?: string
  switcherButtonClassName?: string
  switcherClassName?: string
  compact?: boolean
  children?: (
    organizationName: string,
    state: OrganizationButtonState
  ) => React.ReactNode
}

const OrganizationButton = ({
  className,
  wrapperClassName,
  switcherButtonClassName,
  switcherClassName,
  compact = false,
  children,
}: OrganizationButtonProps) => {
  const [auth] = useAuth()
  const navigate = useNavigate()
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false)
  const [isJoinDialogOpen, setIsJoinDialogOpen] = useState(false)

  const organizationName = useOrganizationName()

  const hasCompany = Boolean(auth.user?.customer_id)

  const { canSwitchCompany } = useCompanySelection()
  const areJoinRequestsEnabled = useFeatureFlag(FLAGS.COMPANY_JOIN_REQUESTS)
  const areMembershipsEnabled = useFeatureFlag(FLAGS.COMPANY_MULTI_ACCESS)
  const canJoinCompany = areJoinRequestsEnabled && areMembershipsEnabled
  const canCreateCompany = useCanCreateAnotherCompany()
  const hasPendingRequests = useMyPendingJoinRequests().length > 0

  const isAwaitingApproval = !hasCompany && hasPendingRequests
  const hasPendingRequestsElsewhere = hasCompany && hasPendingRequests
  const canJoinAnotherCompany = hasCompany && canJoinCompany
  const canOpenSwitcher =
    canSwitchCompany ||
    hasPendingRequestsElsewhere ||
    canCreateCompany ||
    canJoinAnotherCompany
  const isSplit = canOpenSwitcher && !compact

  const toggleSwitcher = useCallback(
    () => setIsSwitcherOpen((open) => !open),
    []
  )

  const openPage = useCallback(() => {
    navigate(hasCompany ? COMPANY_PROFILE_PATH : CREATE_COMPANY_PATH)
  }, [hasCompany, navigate])

  const switcherTitle = hasPendingRequestsElsewhere
    ? 'Switch company — you have a pending join request'
    : 'Switch company'

  const pendingBadge = hasPendingRequestsElsewhere && (
    <NavBadge
      label="You have a pending join request"
      cy="organization-button-pending"
      className="absolute top-1 right-1"
    />
  )

  const keepSwitcherOpenOnPress = (event: React.MouseEvent) => {
    if (isSwitcherOpen) event.stopPropagation()
  }

  return (
    <>
      <div
        className={cn(
          'relative',
          isSplit && 'flex min-w-0 items-center gap-0.5',
          wrapperClassName
        )}
      >
        <button
          type="button"
          data-cy="organization-button"
          onClick={canOpenSwitcher && compact ? toggleSwitcher : openPage}
          onMouseDown={compact ? keepSwitcherOpenOnPress : undefined}
          title={
            canOpenSwitcher && compact
              ? `${organizationName} — ${switcherTitle.toLowerCase()}`
              : hasCompany
                ? `${organizationName} — company profile`
                : isAwaitingApproval
                  ? `${organizationName} — view your request`
                  : organizationName
          }
          className={cn('relative min-w-0', className)}
        >
          {children ? (
            children(organizationName, { isAwaitingApproval })
          ) : (
            <>
              {isAwaitingApproval && (
                <ClockIcon className="size-4 shrink-0 text-status-warning-900" />
              )}
              <span className="max-w-[140px] truncate">{organizationName}</span>
            </>
          )}
          {compact && pendingBadge}
        </button>

        {isSplit && (
          <button
            type="button"
            data-cy="organization-switcher-button"
            onClick={toggleSwitcher}
            onMouseDown={keepSwitcherOpenOnPress}
            title={switcherTitle}
            aria-label={switcherTitle}
            aria-haspopup="menu"
            aria-expanded={isSwitcherOpen}
            className={cn(
              'relative flex shrink-0 items-center justify-center',
              switcherButtonClassName
            )}
          >
            <ChevronUpDownIcon
              data-cy="organization-button-chevron"
              className="size-4 shrink-0"
            />
            {pendingBadge}
          </button>
        )}

        {isSwitcherOpen && (
          <Suspense fallback={null}>
            <CompanySwitcher
              className={switcherClassName}
              companyProfilePath={COMPANY_PROFILE_PATH}
              onCreateCompany={
                canCreateCompany
                  ? () =>
                      navigate(CREATE_COMPANY_PATH, {
                        state: ADD_COMPANY_STATE,
                      })
                  : undefined
              }
              createCompanyLabel={
                canJoinCompany
                  ? 'Create or join a company'
                  : 'Create another company'
              }
              onJoinCompany={
                canJoinCompany && !canCreateCompany
                  ? () => setIsJoinDialogOpen(true)
                  : undefined
              }
              onClose={() => setIsSwitcherOpen(false)}
            />
          </Suspense>
        )}
      </div>

      {isJoinDialogOpen && (
        <Suspense fallback={null}>
          <JoinCompanyDialog onClose={() => setIsJoinDialogOpen(false)} />
        </Suspense>
      )}
    </>
  )
}

export default OrganizationButton
